/* eslint-disable @typescript-eslint/no-explicit-any */
import { useForm } from 'react-hook-form';
import { useRef, useState } from 'react';
import * as z from 'zod';
import { SignupSchema } from "@/utils/FormUtils/FormSchema";
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CardWithNoFooter } from "@/components/ui/CardWithNoFooter";
import { AuthCard } from '@/components/Auth/AuthCard';
import { useQueryClient } from '@tanstack/react-query';
import { LinearProgress } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { DevUser } from 'openapi/api/model';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { AxiosError } from 'axios';
import { resendConfirmationCode, useConfirmSingup, useSignUpExperimental } from '../AuthUtils/authHooks';
import { useCreateAccount } from '@/utils/Auth/Session';
import { ObjectType } from '../AuthUtils/authTypes';
import {Icon} from 'react-icons-kit';
import {eyeOff} from 'react-icons-kit/feather/eyeOff';
import {eye} from 'react-icons-kit/feather/eye'
import { Label } from '@/components/ui/label';




export const SignUp = ({setConfirmationStage}:{setConfirmationStage:()=>void}) => {
  const [pwdState, setPwdState] = useState<{
    password: { type: string; icon: JSX.Element };
    confirmPassword: { type: string; icon: JSX.Element };
  }>({
    password: { type: 'password', icon: eyeOff },
    confirmPassword: { type: 'password', icon: eyeOff }
  });
  
  const { register, handleSubmit, formState: {errors}, getValues } = useForm<z.infer<typeof SignupSchema>>({
    resolver: zodResolver(SignupSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
    }
  });

  const navigate=useNavigate({from:'/auth'})
  const [,setSignupCodeStatus]=useState<'idle' | 'in progress'>('idle')
  const queryClient=useQueryClient()
  const resp=queryClient.getQueryData(['Auth']) as ObjectType
  const sub=resp?.cognitoUser.signInUserSession?.idToken.payload.sub as string
  console.log('Sub: ', sub)
  const devUser:DevUser={
    email:getValues('email'),
    userName:getValues('userName'),
    sub:sub,
    github_ID:getValues('userName'),
    bio:'Your bio/experience'
  }
  //const {mutate:Login,isSuccess:loginSuccess}=useSignIn({queryClient})
  const {createAccount,errors:createAccountErrs,loading}=useCreateAccount({
    queryClient, 
    devUser:devUser,
    onSuccess:()=>{
      navigate({to:'/home'})
    }
  })
  const { register:confirmationCodeRegister, handleSubmit:confirmSubmit  } = useForm()
  const {mutateAsync:confirmSignUp}=useConfirmSingup()
  const {mutateAsync:SignUp,isSuccess:signupSuccess,isPending:signUpLoading,status:signUpStatus}=useSignUpExperimental()

  if(createAccountErrs){
    toast.error("Unable to login due to an error!",{
      description: `${createAccountErrs}`,
      action: {
        label: "Dismiss",
        onClick: () => {},
      },
      style: {
        background: '#dc2626'
      }
    })
  }

  const submitRef = useRef(null) as any
  const _triggerSubmit = () => {
    submitRef?.current?.click(); // Programmatically click the hidden submit button
  };


  const onSubmit = async (data:any) => {
    try{
      await SignUp({
        name:data.userName,
        email:data.email,
        password:data.password,
        phone_number:'+254712345678',
      })
      if (signUpStatus === 'success'){
        setConfirmationStage();
      }
    }catch(err){
      toast.error("Unable to submit!",{
        description: `${(err as AxiosError).message}`,
        action: {
          label: "Dismiss",
          onClick: () => {},
        },
        style: {
          background: '#dc2626'
        }
      })
    }
  };

  
  const resendConfirmationCodeFunc=()=>{
    const userName=getValues('userName')
    try{
      resendConfirmationCode({userName:userName})
      toast.success("Code resent!",{
        description: "Confirmation code resent successfully",
        action: {
          label: "Dismiss",
          onClick: () => {},
        },
        style: {
          background: '#a3e635'
        }
      })
    }catch(e){
      toast('Try again later', {
        description: e as string,
        action: {
          label: "Dismiss",
          onClick: () => {},
        },
      });
    }
    
  }

  const confirmEmail = async (data:any) => {
    const userName=getValues('userName');
    const password=getValues('password')
    setSignupCodeStatus('in progress')
    try{
      await confirmSignUp({
          code:data.confirmationCode,
          userName,
          password
        })
      setSignupCodeStatus('idle')
      await createAccount()
    }catch(e){
      toast.error("Unable to sign up due to an error!",{
        description: `${e}`,
        action: {
          label: "Dismiss",
          onClick: () => {},
        },
        style: {
          background: '#dc2626'
        }
      })
      throw ({confirmEmailErr:e})
    }
    

  }


  const handlePasswordVisibility = (field: 'password' | 'confirmPassword') => {
    setPwdState(prevState => ({
      ...prevState,
      [field]: {
        type: prevState[field].type === 'password' ? 'text' : 'password',
        icon: prevState[field].type === 'password' ? eye : eyeOff
      }
    }));
  };
  


  if(signupSuccess){
    return (
      <>
        <CardWithNoFooter 
        cardTitle="Confirm e-mail"
        cardDescription="Enter the confirmation code sent to your email"
        classname="bg-white">
          <form onSubmit={confirmSubmit(confirmEmail)} className="flex flex-col space-y-1.5 bg-white">
            <Input  id={'confirmEmail'} type={'text'} {...confirmationCodeRegister('confirmationCode')} name={'confirmationCode'}/>
            <div className="min-h-40">
            {loading && <LinearProgress/> }
            </div>
            <Button type='submit'>Confirm</Button>
          </form>
          <Button
          variant={'link'}
          onClick={resendConfirmationCodeFunc}>
            Resend code
          </Button>
        </CardWithNoFooter> 
        <Toaster/>
      </>
      
    )
  }


  return (
    <AuthCard 
    loading={signUpLoading}
    cardTitle="Create an account"
    cardDescription="Enter your email and password to create an account"
    buttonText="Continue"
    onSubmit={_triggerSubmit}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid w-full items-center gap-4">
          <div className="flex flex-col space-y-1.5" key="userName">
            <Label htmlFor="userName">Username</Label>
            <Input id="userName" type="text" {...register("userName")} name="userName"/>
            {errors["userName"] && errors["userName"].message && <p className="text-red-500 text-sm">{errors["userName"].message}</p>}
          </div>
          <div className="flex flex-col space-y-1.5" key="email">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" {...register("email")} name="email"/>
            {errors["email"] && errors["email"].message && <p className="text-red-500 text-sm">{errors["email"].message}</p>}
          </div>
          <div className="flex flex-col space-y-1.5" key="password">
            <Label htmlFor="password">Password</Label>
            <div className="flex">
              <Input id="password" type={pwdState.password.type} {...register("password")} name="password"/>
              <span className="flex justify-around items-center" onClick={() => handlePasswordVisibility('password')}>
                <Icon className="absolute mr-10" icon={pwdState.password.icon} size={15}/>
              </span>
            </div>
            {errors["password"] && errors["password"].message && <p className="text-red-500 text-sm">{errors["password"].message}</p>}
          </div>
          <div className="flex flex-col space-y-1.5" key="confirmPassword">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <div className="flex">
              <Input id="confirmPassword" type={pwdState.confirmPassword.type} {...register("confirmPassword")} name="confirmPassword"/>
              <span className="flex justify-around items-center" onClick={() => handlePasswordVisibility('confirmPassword')}>
                <Icon className="absolute mr-10" icon={pwdState.confirmPassword.icon} size={15}/>
              </span>
            </div>
            {errors["confirmPassword"] && errors["confirmPassword"].message && <p className="text-red-500 text-sm">{errors["confirmPassword"].message}</p>}
          </div>
        </div>
        <input type="submit" value="Submit" style={{ display: 'none' }} ref={submitRef} />
      </form>
    </AuthCard>
  )


}

